/**
 * Bloqueo TEMPORAL de módulos durante la transición legacy → SIME nuevo.
 *
 * Mientras un módulo no esté terminado / pasando todas sus pruebas, NO debe
 * mostrarse a los usuarios prácticos (supervisores, encargados, operadores) en
 * el frontend. Solo el ADMINISTRADOR lo ve, para verificar/probar.
 *
 * Este es un eje de "readiness" (¿está listo para mostrarse?), SEPARADO del eje
 * de autorización por rol (`roles` en el sidebar / `requireRole` en la API). El
 * backend NO se toca: el día que un módulo se desbloquea, los permisos de rol
 * existentes se activan solos.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * PARA DESBLOQUEAR UN MÓDULO (cuando quede operativo): borrá su línea en
 * `LOCKED_MODULE_PREFIXES`. Nada más. El sidebar lo vuelve a mostrar y el guard
 * de ruta deja de bloquearlo, respetando los `roles` ya definidos del entry.
 * (Para "Asignar Líderes" y "Mis Actividades" revertí además su test en
 *  sidebar-routes.test.tsx a la aserción de visibilidad original.)
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Coordinadores Codelco (miniapp `/miniapp/*`, `/firma/:uuid`) y Modo Líder
 * (`/l/*`) viven en árboles de rutas FUERA del AppShell → este lock no los
 * alcanza. Por eso `/l` NO está en la lista (bloquearlo dejaría al rol LIDER
 * sin su única app).
 */
import { ROLES } from '@sime/shared';

/**
 * Roles que IGNORAN el lock y ven todos los módulos (terminados o no).
 * Solo el ADMINISTRADOR (el dueño, para verificar). Los supervisores son los
 * usuarios prácticos y quedan restringidos hasta que cada módulo esté listo.
 */
export const MODULE_LOCK_BYPASS_ROLES: readonly number[] = [ROLES.ADMINISTRADOR];

export function bypassesModuleLocks(roleId: number | null | undefined): boolean {
  return roleId != null && MODULE_LOCK_BYPASS_ROLES.includes(roleId);
}

/**
 * Prefijos de ruta de los módulos AÚN NO operativos (ocultos del sidebar y
 * bloqueados a nivel de ruta para no-admins). Cada entrada es un módulo
 * top-level; cubre también sus sub-rutas de detalle (ej. `/planes/:id`).
 *
 * Operativos (siempre visibles, NO van acá): Inicio, Programación, Plan Matriz,
 * Pautas, Tickets (+submódulos), Personal (+submódulos), Reportes (+submódulos).
 */
export const LOCKED_MODULE_PREFIXES: readonly string[] = [
  '/asignar-lideres', // Asignar Líderes
  '/actividades-previas', // Actividades Previas
  '/planes', // Planes y Programas
  '/mis-actividades', // Mis Actividades (familia Planes)
  '/seguridad', // Seguridad → Peligros + Documental
  '/resso', // Seguridad → RESSO
  '/charlas', // Seguridad → Comunicación
];

/**
 * ¿La ruta cae dentro de un módulo bloqueado? Match por LÍMITE DE SEGMENTO
 * (`=== p` o empieza con `p + '/'`) para no atrapar falsos positivos:
 * `/planes` NO matchea `/plan-matriz`, y un eventual `/l` no matcharía `/lotes`.
 */
export function isPathLocked(pathname: string): boolean {
  return LOCKED_MODULE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(prefix + '/'),
  );
}

/**
 * ¿Hay que ocultar/bloquear esta ruta para este usuario? Los roles con bypass
 * (ADMINISTRADOR) nunca se ven afectados.
 */
export function isNavPathLocked(
  path: string,
  roleId: number | null | undefined,
): boolean {
  return !bypassesModuleLocks(roleId) && isPathLocked(path);
}
